import { Button, Typography, useMediaQuery } from '@mui/material'
import { DownloadOutlined } from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react'
import gaiaTheme from '../../../../root-components/theme'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display'
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service'
import { IReportPeriod } from '../../../../shared/services/report-period/report-period.model'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { DownloadStatusContext } from '../../../../context/download-status/download-status'
import FileDownloadService from '../../../../shared/services/file-download/file-download.service'
import { LoadingPage } from '../../loading-page'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'

export const ReportingAuditPackPage = () => {
  const reportPeriodService = new ReportPeriodService()
  const fileDownloadService = new FileDownloadService()
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const userNotificationsContext = useContext(UserNotificationsContext)
  const downloadStatusContext = useContext(DownloadStatusContext)
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'))

  const [reportPeriods, setReportPeriods] = useState<IReportPeriod[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [perPage, setPerPage] = useState(100)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const companyName = (!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient)
    ? userDetails?.partnerOrganisation?.partnerName
    : 'Gaia'

  useEffect(() => {
    reportPeriodService.getAllReportPeriod()
      .then((response) => {
        const sorted = [...response].sort((a, b) => Number(b.calendarYear) - Number(a.calendarYear))
        setReportPeriods(sorted)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setReportPeriods([])
        setIsLoading(false)
      })
  }, [])

  // One in-flight pack per period: without the guard every extra click queued another
  // identical export behind the first.
  const [queuedPeriodIds, setQueuedPeriodIds] = useState<string[]>([])

  const downloadHandler = (reportPeriodId: string) => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to generate an Audit Pack.`, buttonCopy: 'Ok' })
      return
    }
    if (queuedPeriodIds.includes(reportPeriodId)) return
    setQueuedPeriodIds(ids => [...ids, reportPeriodId])
    fileDownloadService.createFileDownloadEntry('auditPackXlsx', reportPeriodId, null)
      .then((response) => downloadStatusContext.startDownloadTracking(response.id))
      .catch((err) => {
        console.log(err)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
      .finally(() => setQueuedPeriodIds(ids => ids.filter(id => id !== reportPeriodId)))
  }

  const headCells: IHeadCell[] = [
    { id: 'reportPeriod', numeric: false, label: 'Report Period', sortable: false, isHeading: true },
    { id: 'action', numeric: false, align: 'right', label: 'Audit Pack', sortable: false },
  ]

  const rows = reportPeriods.map((reportPeriod) => ({
    id: reportPeriod.id,
    reportPeriod: reportPeriod.friendlyName || reportPeriod.calendarYear,
    action: (
      <Button
        variant='outlined'
        startIcon={<DownloadOutlined />}
        onClick={() => downloadHandler(reportPeriod.id)}
        disabled={queuedPeriodIds.includes(reportPeriod.id)}
        sx={{ textTransform: 'none' }}
      >
        {queuedPeriodIds.includes(reportPeriod.id) ? 'Preparing\u2026' : 'Download'}
      </Button>
    ),
  }))

  return (
    <PageOuterContainer>
      <PageHeading
        title='External Audit Pack'
        includeBackButton={true}
        backButtonLink='/reporting'
        backButtonText='Back to reporting'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        Download an Excel audit pack for a reporting period: the full emissions register with data sources and
        emission factors, a methodology summary, the change log and scope totals. Generated files also appear in
        Settings → Downloaded Files.
      </Typography>
      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          isMobileMode={isMobileMode}
          selected={selected}
          setSelected={setSelected}
          totalRecords={rows.length}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>
    </PageOuterContainer>
  )
}
